import {useQueryClient} from '@github-ui/react-query'
import {getHighlightedEvent, isEventHighlighted} from '@github-ui/timeline-items/HighlightedEvent'
import {useCallback, useMemo, useState} from 'react'

import {
  type TimelineIssue,
  type TimelineItemsConnection,
  type TimelineItemsPageParams,
  timelineItemsQueryKey,
  timelineItemsQueryKeyPrefix,
  timelineQueryOptions,
} from './fetch-timeline-items'
import type {IssueTimelineItemFragment} from './IssueTimelineItem'
import {
  assembleTimelineItems,
  countItemsRemaining,
  dedupeItems,
  isHighlightReady,
  subtractItems,
  type TimelineCounters,
  type TimelineSlot,
} from './timeline-assembly'
import {asTimelineItem} from './timeline-item-parity'
import {
  anchoredBackParamsFor,
  asPinnedTimelineWindow,
  DEFAULT_TIMELINE_PAGE_SIZE,
  frontParamsFor,
  unanchoredBackParamsFor,
} from './timeline-prefill'
import {overlaid, pendingCountDelta, withCorrelationKeys} from './timeline-write-overlay'
import type {UseTimelineItemsCoreResult} from './use-timeline-items-core'
import {type TimelineSection, useLoadedPages, useTimelineSections} from './use-timeline-sections'
import {useTimelineWrites} from './use-timeline-writes'

export type UseTimelineItemsTanstackProps = {
  owner: string
  repo: string
  number: number
  /** Owner of the timeline connection for optimistic overlays. */
  issueId: string
  highlightedEvent?: string
  /** Offset-based crawler page from `?timeline_page=N`. */
  page?: number
  /** Matches the Relay path's first page size so both produce the same initial view. */
  pageSize?: number
  /**
   * Cursor the newest section pins itself to, from the page payload. Without one the section falls
   * back to discovering it, which costs the round trip hydration exists to remove.
   */
  initialBackAnchor?: string
}

type LoadMoreOptions = {onComplete?: () => void}

const connectionOf = (issue: TimelineIssue | undefined) => issue?.timelineItems ?? undefined

const isPresent = <T>(value: T | undefined): value is T => value !== undefined

const FOCUS_NEIGHBOR_COUNT = 3

function edgeNodes(connection: TimelineItemsConnection | undefined): IssueTimelineItemFragment[] {
  if (!connection) return []

  return (connection.edges ?? []).flatMap(edge => (edge?.node ? [asTimelineItem(edge.node)] : []))
}

/**
 * The section holding the end of the timeline, and so the one a comment the reader posts belongs
 * in. Its pages, opened with "load more", read older rows and are not the end.
 */
const isEndSection = (section: TimelineSection | undefined) => Boolean(section?.isBase) && section?.role === 'back'

/** The permalinked comment with its neighbours, and whatever "load more" has opened either side. */
function highlightWindowItems(
  focused: TimelineItemsConnection | undefined,
  before: TimelineItemsConnection[],
  after: TimelineItemsConnection[],
): IssueTimelineItemFragment[] {
  if (!focused) return []

  return dedupeItems([...before.flatMap(edgeNodes), ...edgeNodes(focused), ...after.flatMap(edgeNodes)])
}

/**
 * Extend the focus window only by unique earlier rows: re-anchoring can overlap retained pages.
 */
function highlightStartPosition(
  beforeFocusCount: number,
  before: TimelineItemsConnection[],
  focused: TimelineItemsConnection | undefined,
): number {
  const earlierItems = subtractItems(dedupeItems(before.flatMap(edgeNodes)), edgeNodes(focused))
  return Math.max(beforeFocusCount - earlierItems.length, 0)
}

/**
 * # How the timeline is read
 *
 * The timeline is too long to fetch whole, so it is read as a few **sections** with gaps between
 * them. Each section is one read of the endpoint. `use-timeline-sections.ts` performs the reads;
 * this file decides what to ask for and stitches the answers into one list of rows.
 *
 * ## The sections
 *
 * **front** -- the oldest comments, where the timeline starts. Opens with `{first: 15}`
 * (`DEFAULT_PAGE_SIZE`, overridable by the caller). "Load more" never enlarges it: it adds a
 * separate section reading `{first: N, after: <the last row it holds>}`, rendered below it.
 *
 * **back** -- the newest comments, 15 of them. **It exists on every issue**, whatever the
 * timeline's length, so the end of the timeline is always a section: that is where a comment the
 * reader posts belongs, and it has to stay there across refetches. "Load more" upward adds a
 * section reading `{last: N, before: <the first row it holds>}`.
 *
 * **highlight** -- a group around a permalinked comment: the comment itself plus
 * `FOCUS_NEIGHBOR_COUNT` either side, so 7 rows, with its own "load more" on each side.
 *
 * Both base sections are read at once. On a timeline the front section already covers in full the
 * two overlap completely and the reader sees one unbroken list, `dedupeItems` collapsing the
 * overlap; on a longer one there is a gap between them, and the "N remaining items" marker says how
 * much. A "load more" section holds 150 rows (`VALUES.timeline.pageSize`), or the whole remainder
 * when fewer are left, so a gap of any ordinary size closes in one activation.
 *
 * ## The anchor
 *
 * The back section is pinned to a cursor rather than read as `{last: 15}`, because a `last: N`
 * window **slides**: every comment that arrives pushes an older one out of it and into the gap, so
 * a reader watching the newest comments sees the top one vanish. Anchored, the window **grows** --
 * the marker does not move, because the timeline grew by one and so did the rows on screen.
 *
 * `after: X` returns the rows *after* the one `X` points at, so pinning the section needs the
 * cursor of the row above its first -- the 16th from the end. That cursor is the **anchor**, and
 * every read the section makes is `{last: MAX_PAGE_SIZE, after: anchor}`, under one key that never
 * changes again.
 *
 * The page query returns that row, so `timeline-prefill.ts` hands the anchor in and writes the
 * window it pins into the cache before this hook runs: the section opens already holding its rows.
 * Only a payload that cannot supply one falls back to reading `{last: 16}` to discover it.
 *
 * Because the anchored read asks for the endpoint's cap from the start, it is never sized from the
 * timeline's length, and ten comments arriving at once are absorbed by the request that absorbs
 * one. Growth past `MAX_PAGE_SIZE` rows beyond the anchor falls outside the section and becomes an
 * ordinary gap.
 *
 * ## Where a write goes
 *
 * A comment the reader posts has to be on screen before the server has confirmed anything.
 * `use-timeline-writes.ts` arranges that; what matters here is which sections it reaches.
 *
 * An added row belongs at the end of the timeline, so it is applied to the back section alone.
 * `{at: 'end'}` means the end of whichever section it is applied to, so applying it everywhere
 * would put the row at the end of the *oldest* one -- on a gapped timeline, at the marker in the
 * middle of the list rather than under the newest comment. Deletes and edits do go to every
 * section, because the row they name can be anywhere.
 *
 * The row is overlaid onto the back section, written into it when the mutation succeeds, and read
 * back from it -- the same section throughout, because it always exists and its key stops changing
 * after its first read.
 */

/**
 * TanStack implementation of the timeline, selected by `issue_viewer_paved_path`. Returns
 * the same shape as the Relay hook, so the flag flips at a single call site and the rendering path
 * never learns which source it read from.
 *
 * Crawler pages ask the endpoint for one explicit page. Interactive timelines read the sections
 * described below.
 *
 * Like the Relay path it reads two sections -- oldest and newest -- with a "load more" gap between
 * them, plus a third around a deep-linked event. Every one of them, and every page opened with
 * "load more", is a section in one list: params in, a result out. Comment writes are overlaid
 * optimistically and written into the cache when the mutation settles.
 */
export function useTimelineItemsTanstack({
  owner,
  repo,
  number,
  issueId,
  highlightedEvent,
  page,
  pageSize = DEFAULT_TIMELINE_PAGE_SIZE,
  initialBackAnchor,
}: UseTimelineItemsTanstackProps): UseTimelineItemsCoreResult {
  const queryClient = useQueryClient()
  const highlight = useMemo(() => getHighlightedEvent(highlightedEvent), [highlightedEvent])
  const focusText = highlight ? `${highlight.prefix}-${highlight.id}` : undefined
  const frontParams = useMemo(() => frontParamsFor(pageSize), [pageSize])

  const queryOptions = useCallback(
    (params: TimelineItemsPageParams) => timelineQueryOptions(owner, repo, number, params),
    [owner, repo, number],
  )

  const loadedPages = useLoadedPages(focusText)

  /**
   * Most hosts have no anchor handed to them. Only the issue route prefills from a page payload;
   * the eight others that render the viewer -- the dashboard and memex side panels, workstreams,
   * the duplicate panel, the Copilot previews -- mount it with no payload to derive one from. There
   * the newest section reads `{last: pageSize + 1}` until it gets that many rows, takes the cursor
   * of the extra row, and is pinned from then on. A shorter timeline has no row outside its visible
   * window to use as an anchor, so it remains unanchored as it grows. Deploy skew reaches the same
   * path: a payload predating the extra row cannot supply an anchor either.
   *
   * Whichever anchor is already in use wins. A discovered one only exists where no payload anchor
   * was available, and a later payload that *can* supply one names a different row -- re-pinning
   * the window and taking what the reader is looking at with it.
   */
  const [discovered, setDiscovered] = useState<{issueKey: string; cursor: string}>()
  // Kept per issue: the hosts that discover their own anchor are also the ones that re-render this
  // hook with a different issue rather than remounting it, and a cursor names a row in one timeline
  // only. Carried across, it would pin the next issue's window to a row from the last one.
  const issueKey = `${owner}/${repo}/${number}`
  const discoveredBackAnchor = discovered?.issueKey === issueKey ? discovered.cursor : undefined
  const backAnchor = discoveredBackAnchor ?? initialBackAnchor

  const crawlerParams: TimelineItemsPageParams | undefined = useMemo(
    () => (page ? {timelinePage: page} : undefined),
    [page],
  )

  // Unanchored until an extra row exists outside the visible window. Built by the same functions
  // the prefill writes under, so a stored window is found rather than fetched again under a key
  // differing by a character.
  const backParams: TimelineItemsPageParams = useMemo(
    () => (backAnchor ? anchoredBackParamsFor(backAnchor) : unanchoredBackParamsFor(pageSize)),
    [backAnchor, pageSize],
  )

  // In the order they render: the oldest rows, then the permalink window sitting in the gap, then
  // the newest. `select` reads back along this order, so it is the section order, not a listing.
  const sections = useMemo(
    (): TimelineSection[] =>
      crawlerParams
        ? [{role: 'front', params: crawlerParams, isBase: true}]
        : [
            {role: 'front', params: frontParams, isBase: true},
            ...loadedPages.front.map((params): TimelineSection => ({role: 'front', params})),
            ...loadedPages.highlight.filter(section => section.role === 'highlight-before'),
            ...(focusText
              ? [
                  {
                    role: 'highlight' as const,
                    params: {focusText, first: 1, focusNeighborCount: FOCUS_NEIGHBOR_COUNT},
                    isBase: true,
                  },
                ]
              : []),
            ...loadedPages.highlight.filter(section => section.role === 'highlight-after'),
            // Pages read backward from the newest end are older than it, so they render before it.
            ...loadedPages.back.map((params): TimelineSection => ({role: 'back', params})),
            {role: 'back', params: backParams, isBase: true},
          ],
    [crawlerParams, frontParams, backParams, focusText, loadedPages.front, loadedPages.back, loadedPages.highlight],
  )

  const {reads, loading, select, baseIndex} = useTimelineSections({owner, repo, number, sections})
  const frontBaseIndex = baseIndex('front')
  const fetchedFrontConnection = connectionOf(reads[frontBaseIndex])

  const backBaseIndex = baseIndex('back')
  const backBaseRead = reads[backBaseIndex]
  const backBaseConnection = connectionOf(backBaseRead)

  // Only from a read of the issue being rendered. Switching issues re-renders this hook before the
  // reads catch up, so for one render the newest section still holds the previous issue's rows --
  // and taking a cursor from those would pin this issue's window to a row in someone else's
  // timeline.
  const backBaseEdges = backBaseConnection?.edges ?? []
  if (
    backBaseRead?.id === issueId &&
    !backAnchor &&
    backBaseEdges.length === pageSize + 1 &&
    backBaseConnection?.pageInfo?.startCursor
  ) {
    const cursor = backBaseConnection.pageInfo.startCursor
    // Re-key atomically: without a value under the anchored key, the unanchored read disappears
    // for the render that starts the pinned read. That can briefly leave only the stale oldest
    // count, or no rows at all. Marking this snapshot stale preserves the completed probe while
    // still making the pinned query confirm it with the endpoint immediately.
    queryClient.setQueryData<TimelineIssue>(
      timelineItemsQueryKey(owner, repo, number, anchoredBackParamsFor(cursor)),
      {...backBaseRead, timelineItems: asPinnedTimelineWindow(backBaseConnection)},
      {updatedAt: 0},
    )
    setDiscovered({issueKey, cursor})
  }

  // The newest section always holds the end of the timeline, so it is always where a write goes.
  // Memoized because it is handed to the live-update subscription, whose Relay config is compared by
  // identity: a fresh array each render tears the subscription down and re-establishes it.
  const endSectionQueryKey = useMemo(
    () => timelineItemsQueryKey(owner, repo, number, backParams),
    [owner, repo, number, backParams],
  )
  const timelineKeyPrefix = useMemo(() => timelineItemsQueryKeyPrefix(owner, repo, number), [owner, repo, number])

  // A read has come back with the row this add is still pending for.
  const {endSectionOperations, everySectionOperations, correlationKeys} = useTimelineWrites({
    issueId,
    endSectionQueryKey,
    timelineKeyPrefix,
    reads,
  })

  // Where a comment the reader posted appears before the server has confirmed it: the pending
  // operation is applied over the rows as they are read. `applyOperations` does the inserting, at
  // the position the mutation asked for -- `{at: 'end'}` for a new comment.
  const connections = useMemo(
    () =>
      reads.map((read, index) => {
        const operations = isEndSection(sections[index]) ? endSectionOperations : everySectionOperations
        return withCorrelationKeys(connectionOf(overlaid(read, operations)), correlationKeys)
      }),
    [reads, sections, endSectionOperations, everySectionOperations, correlationKeys],
  )

  const frontPages = useMemo(() => select(connections, 'front').filter(isPresent), [select, connections])

  const frontItems = useMemo(() => dedupeItems(frontPages.flatMap(edgeNodes)), [frontPages])

  // The newest section's own total, moved by whatever is still pending. The pending part is summed
  // from the operations rather than read off an overlaid section, because an overlay moves a count
  // only for a section that held the row.
  const serverCount = connectionOf(reads[baseIndex('back')])?.totalCount
  const totalItemCount = Math.max(
    (serverCount ?? fetchedFrontConnection?.totalCount ?? 0) + pendingCountDelta(endSectionOperations, reads),
    0,
  )

  const backPages = useMemo(() => select(connections, 'back').filter(isPresent), [select, connections])

  const backIsLoading = loading[baseIndex('back')] ?? false

  const backItems = useMemo(
    () => subtractItems(dedupeItems(backPages.flatMap(edgeNodes)), frontItems),
    [backPages, frontItems],
  )

  const loadedItems = useMemo(() => [...frontItems, ...backItems], [frontItems, backItems])

  const highlightIsPreloaded = useMemo(
    () => (highlight ? loadedItems.some(event => isEventHighlighted(event, highlight)) : false),
    [loadedItems, highlight],
  )

  const highlightIndex = baseIndex('highlight')
  const highlightData = connections[highlightIndex]

  const highlightBefore = useMemo(
    () => select(connections, 'highlight-before').filter(isPresent),
    [select, connections],
  )
  const highlightAfter = useMemo(() => select(connections, 'highlight-after').filter(isPresent), [select, connections])

  const highlightItems = useMemo(
    () => highlightWindowItems(highlightData, highlightBefore, highlightAfter),
    [highlightData, highlightBefore, highlightAfter],
  )

  const startPosition = highlightStartPosition(
    connectionOf(reads[highlightIndex])?.beforeFocusCount ?? 0,
    highlightBefore,
    highlightData,
  )

  const uniqueHighlightItems = useMemo(
    () => (highlightItems.length > 0 ? subtractItems(highlightItems, loadedItems) : []),
    [highlightItems, loadedItems],
  )

  const hasLazyLoadedHighlights = uniqueHighlightItems.length > 0

  const timelineCounters: TimelineCounters = useMemo(
    () => ({
      frontItems: frontItems.length,
      backItems: backItems.length,
      highlightItems: uniqueHighlightItems.length,
      highlightStartPosition: startPosition,
    }),
    [frontItems.length, backItems.length, uniqueHighlightItems.length, startPosition],
  )

  const {front: itemsRemainingFront, back: itemsRemainingBack} = countItemsRemaining({
    hasLazyLoadedHighlights,
    timelineCounters,
    totalItemCount,
    frontItemCount: frontItems.length,
    backItemCount: backItems.length,
  })

  // One request per activation; the endpoint clamps the page size it is given.
  const loadPages = useCallback(
    (
      count: number,
      /** The page to continue from: the one furthest in the direction being read. */
      from: TimelineItemsConnection | undefined,
      paramsFor: (from: TimelineItemsConnection, size: number) => TimelineItemsPageParams | null,
      apply: (params: TimelineItemsPageParams) => void,
      options?: LoadMoreOptions,
    ) => {
      void (async () => {
        const params = from && paramsFor(from, count)
        // `LoadMore` clears its own loading state only in `onComplete`, so every path has to reach
        // it -- including this one, or the control stays disabled with nothing in flight.
        if (!params) return options?.onComplete?.()

        try {
          const connection = connectionOf(await queryClient.fetchQuery(queryOptions(params)))
          // The rows are already in the cache; holding the params is what keeps the page live.
          if (connection && (connection.edges ?? []).length > 0) apply(params)
        } catch {
          // Keep the marker in place so the read can be retried.
        }

        options?.onComplete?.()
      })()
    },
    [queryClient, queryOptions],
  )

  const loadMoreFrontItems = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      loadPages(
        count,
        frontPages.at(-1),
        (from, size) =>
          from.pageInfo?.hasNextPage && from.pageInfo.endCursor ? {first: size, after: from.pageInfo.endCursor} : null,
        loadedPages.addFront,
        options,
      )
    },
    [loadPages, frontPages, loadedPages.addFront],
  )

  const loadMoreBackItems = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      loadPages(
        count,
        backPages.at(0),
        // Not `hasPreviousPage`: the resolver only counts that when `last` slices what the cursors
        // selected, so an anchored section reports false however many rows precede it.
        (from, size) => (from.pageInfo?.startCursor ? {last: size, before: from.pageInfo.startCursor} : null),
        loadedPages.addBack,
        options,
      )
    },
    [loadPages, backPages, loadedPages.addBack],
  )

  const loadBeforeHighlight = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      if (!highlightData) return options?.onComplete?.()

      // Continues from the last page read, not from the highlight section's own cursor, which
      // would refetch the same page on every activation.
      loadPages(
        count,
        highlightBefore.at(0) ?? highlightData,
        (from, size) =>
          from.pageInfo?.hasPreviousPage && from.pageInfo.startCursor
            ? {last: size, before: from.pageInfo.startCursor}
            : null,
        loadedPages.addHighlight('before'),
        options,
      )
    },
    [loadPages, highlightData, highlightBefore, loadedPages],
  )

  const loadAfterHighlight = useCallback(
    (count: number, options?: LoadMoreOptions) => {
      if (!highlightData) return options?.onComplete?.()

      loadPages(
        count,
        highlightAfter.at(-1) ?? highlightData,
        (from, size) =>
          from.pageInfo?.hasNextPage && from.pageInfo.endCursor ? {first: size, after: from.pageInfo.endCursor} : null,
        loadedPages.addHighlight('after'),
        options,
      )
    },
    [loadPages, highlightData, highlightAfter, loadedPages],
  )

  const slots: TimelineSlot[] | null = useMemo(
    () =>
      assembleTimelineItems({
        frontItems,
        backItems,
        emptyBackItems: [],
        uniqueLoadedHighlightItems: uniqueHighlightItems,
        totalItemCount,
        itemsRemainingFront,
        itemsRemainingBack,
        hasLazyLoadedHighlights,
        loadingInitialBackItems: backIsLoading,
        page,
      }),
    [
      frontItems,
      backItems,
      uniqueHighlightItems,
      totalItemCount,
      itemsRemainingFront,
      itemsRemainingBack,
      hasLazyLoadedHighlights,
      backIsLoading,
      page,
    ],
  )

  const timelineItems = useMemo(
    () =>
      slots?.map(slot => {
        if (slot.type !== 'load-marker') return slot

        const {type: _marker, ...rest} = slot
        return slot.position === 'top'
          ? {
              ...rest,
              type: 'load' as const,
              loadFromTop: loadMoreFrontItems,
              loadFromBottom: hasLazyLoadedHighlights ? loadBeforeHighlight : loadMoreBackItems,
            }
          : {...rest, type: 'load' as const, loadFromTop: loadAfterHighlight, loadFromBottom: loadMoreBackItems}
      }) ?? null,
    [slots, hasLazyLoadedHighlights, loadMoreFrontItems, loadMoreBackItems, loadBeforeHighlight, loadAfterHighlight],
  )

  return {
    timelineItems,
    totalItemCount,
    highlightedReady: isHighlightReady(totalItemCount, backItems.length, timelineCounters, highlightIsPreloaded),
    // Only while the section is anchored. Before a timeline is long enough to supply an anchor, its
    // newest key can still change once the extra row arrives. Narrowing to that key would leave the
    // oldest section stale and can briefly produce a nonzero count with no rows during the re-key.
    newestSectionQueryKey: backAnchor ? endSectionQueryKey : undefined,
  }
}
